import React, { useContext } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import ReactPlayer from "react-player"
import styled, { ThemeContext } from "styled-components"

import Image from "../utils/Image"
import useBreakpoint from "../../hooks/use-breakpoint"
import Box from "./Box"
import ProportionalBox from "./ProportionalBox"
import Link from "../utils/Link"

const ResponsiveReactPlayer = styled(ReactPlayer)`
  position: absolute;
  top: 0;
  left: 0;
`

const AutoWidthReactPlayer = styled(ReactPlayer)`
  width: auto !important; // Resetting width to allow video to resize based on the viewport
`

const Video = React.forwardRef(
  (
    {
      url,
      videoRatio,
      playing,
      loop,
      controls,
      volume,
      light,
      videoThumbnail,
      muted,
      playsinline,
      ...rest
    },
    ref
  ) => {
    let ratio
    switch (videoRatio) {
      case "sixteenNine":
        ratio = 1 / (16 / 9)
        break
      case "fourThree":
        ratio = 1 / (4 / 3)
        break
      case "oneOne":
        ratio = 1
        break
      case "threeFour":
        ratio = 1 / (3 / 4)
        break
      case "nineSixteen":
        ratio = 1 / (9 / 16)
        break
      default:
        ratio = 1 / (16 / 9) // 16:9 by default
    }

    return videoRatio ? (
      // Video Text sections and Popups
      <ProportionalBox ratio={ratio}>
        <ResponsiveReactPlayer
          ref={ref}
          url={url}
          playing={light ? true : playing} // setting autoplay on for light mode to avoid users having to click play button multiple times
          loop={loop}
          controls={controls}
          volume={volume}
          light={light && videoThumbnail ? videoThumbnail : light}
          muted={muted}
          playsinline={playsinline}
          height="100%"
          width="100%"
          {...rest}
        />
      </ProportionalBox>
    ) : (
      // Image Text Carousel and Video Cards sections
      <AutoWidthReactPlayer
        ref={ref}
        url={url}
        playing={light ? true : playing} // setting autoplay on for light mode to avoid users having to click play button multiple times
        loop={loop}
        controls={controls}
        volume={volume}
        light={light && videoThumbnail ? videoThumbnail : light}
        muted={muted}
        playsinline={playsinline}
        {...rest}
      />
    )
  }
)

const ImageOrVideo = props => {
  const {
    desktopImage,
    mobileImage,
    imageLink,
    videoURL,
    mobileVideoURL,
    videoImageFallback,
    isVideoSelected = false,
    alternativeText,
    videoProps,
    imageStyles,
    playing,
    loop,
    videoRatio,
    controls,
    volume,
    ratio,
    light,
    muted,
    playsinline,
    ...rest
  } = props
  const isLink = imageLink?.to || imageLink?.href

  const isMobileBp = useBreakpoint("md")
  const { breakpoints } = useContext(ThemeContext)
  const breakpointTablet = parseInt(breakpoints.md)

  const finalVideoURL =
    isMobileBp && mobileVideoURL?.length > 0 ? mobileVideoURL : videoURL

  return (
    <Box height="100%" width="100%" {...rest}>
      {!isVideoSelected &&
        desktopImage &&
        (!isMobileBp || !mobileImage) &&
        !isLink && (
          <Image
            className="desktop-image"
            {...desktopImage}
            style={{ height: "100%" }}
            imgStyle={imageStyles}
          />
        )}
      {/* if image is link and desktop */}
      {!isVideoSelected &&
        desktopImage &&
        (!isMobileBp || !mobileImage) &&
        isLink && (
          <Link aria-label={`An Image Link`} {...imageLink}>
            <Image
              className="desktop-image"
              {...desktopImage}
              style={{ height: "100%" }}
              imgStyle={imageStyles}
            />
          </Link>
        )}
      {/* Mobile image rendering when not video-selected */}
      {!isVideoSelected && mobileImage && isMobileBp && !isLink && (
        <Image
          className="mobile-image"
          {...mobileImage}
          style={{
            height: "100%",
            maxWidth: `(max-width: ${breakpointTablet}px)`,
          }}
          imgStyle={imageStyles}
        />
      )}
      {/* if image is link and mobile */}
      {!isVideoSelected && mobileImage && isMobileBp && isLink && (
        <Link aria-label={`An Image Link`} {...imageLink}>
          <Image
            className="mobile-image"
            {...mobileImage}
            style={{
              height: "100%",
              maxWidth: `(max-width: ${breakpointTablet}px)`,
            }}
            imgStyle={imageStyles}
          />
        </Link>
      )}
      {isVideoSelected &&
        finalVideoURL &&
        (!videoImageFallback || !isMobileBp) && (
          <Video
            url={finalVideoURL}
            playing={playing}
            loop={loop}
            videoRatio={videoRatio}
            controls={controls}
            volume={volume / 100}
            light={light}
            muted={muted}
            playsinline={playsinline}
            videoThumbnail={videoImageFallback?.url}
            {...videoProps}
          />
        )}
      {/* On mobile, if no finalVideoURL but videoImageFallback exists, render the mobile image */}
      {isVideoSelected &&
        videoImageFallback &&
        isMobileBp &&
        !finalVideoURL && (
          <Image
            className="video-fallback-image"
            {...videoImageFallback}
            style={{ height: "100%" }}
            alternativeText={alternativeText}
            imgStyle={imageStyles}
          />
        )}
    </Box>
  )
}

export default ImageOrVideo

ImageOrVideo.strapiProps = {
  desktopImage: PropTypes.object,
  mobileImage: PropTypes.object,
  mobileVideoURL: PropTypes.string,
  videoURL: PropTypes.string,
  videoImageFallback: PropTypes.object,
  isVideoSelected: PropTypes.bool,
  videoRatio: PropTypes.string,
  controls: PropTypes.bool,
  playing: PropTypes.bool,
  loop: PropTypes.bool,
  volume: PropTypes.number,
  light: PropTypes.bool,
  imageLink: PropTypes.shape(Link.strapiProps),
}

export const query = graphql`
  fragment ImageOrVideo on STRAPI__COMPONENT_ATOMS_IMAGE_OR_VIDEO {
    strapi_id
    desktopImage {
      ...ImageWithConstrainedLayout
    }
    mobileImage {
      ...ImageWithConstrainedLayout
    }
    imageLink {
      ...Link
    }
    videoURL
    mobileVideoURL
    videoImageFallback {
      ...ImageWithConstrainedLayout
    }
    isVideoSelected
    videoRatio
    controls
    playing
    loop
    volume
    light
  }
`
